import { useEffect, useRef } from 'react';

/** Identity of a viewed payload: a payload with a new identity is a new view. */
export type ViewIdentity<T> = (payload: T) => string;

const ONCE_PER_MOUNT: ViewIdentity<unknown> = () => '';

/**
 * Identity of `payload` when it is a view not reported yet; `null` when there
 * is nothing to report — no payload, or the same identity as `lastKey`.
 */
export const newViewKey = <T>(
  lastKey: string | null,
  payload: T | null,
  identify: ViewIdentity<T>,
): string | null => {
  if (payload === null) {
    return null;
  }
  const key = identify(payload);
  return key === lastKey ? null : key;
};

/**
 * Calls `report` the first time `payload` is not `null`, and again whenever
 * `identify` gives it a new identity (by default never — one report per
 * mount). Scoped to the mount: a component that unmounts and mounts again
 * reports again. Pass `null` while the payload is not settled yet.
 */
export const useViewedOnce = <T>(
  payload: T | null,
  report: (payload: T) => void,
  identify: ViewIdentity<T> = ONCE_PER_MOUNT,
): void => {
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    const key = newViewKey(lastKey.current, payload, identify);
    if (key === null || payload === null) {
      return;
    }
    lastKey.current = key;
    report(payload);
  }, [payload, report, identify]);
};
